/**
 * review360SubmissionService.ts
 *
 * CRUD for Review360Submission (hr_v1 path). One doc per (cycle, reviewee,
 * reviewer). Created lazily on first save; finalised on submit.
 *
 * Anonymity enforcement lives in the aggregator (review360Aggregator.ts).
 * This service stores fields as written; the aggregator decides what's
 * exposed downstream. The token doc's `effectiveVisibility` is snapshotted
 * onto every submission so later edits to the template policy never
 * retroactively expose a previously-anonymous reviewer.
 */

import {
  doc, getDoc, setDoc, getDocs,
  query, where, collection, serverTimestamp, runTransaction, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  Review360Submission, Review360Relationship,
  ReviewerVisibility, CompanyValueKey, Score1to5,
} from '../types/eprFramework';
import type { MagicLinkToken } from '../types/magicLinkToken';

const COL = 'review360Submissions';

// ─── Errors ─────────────────────────────────────────────────────────

export class Review360Error extends Error {
  constructor(
    public code: 'not_found' | 'wrong_state' | 'unauthorized' | 'visibility_mismatch',
    message: string,
  ) {
    super(message);
    this.name = 'Review360Error';
  }
}

// ─── Lazy create / get ──────────────────────────────────────────────

export interface OpenSubmissionInput {
  /** Always the magic-link token doc (already verified). */
  token: MagicLinkToken;
  /** Used as the submission doc id — separate from the token. */
  submissionId: string;
  cycleId: string;
  revieweeId: string;
  relationship: Review360Relationship;
  effectiveVisibility: ReviewerVisibility;
  /** Populated only when effectiveVisibility === 'attributed'. */
  reviewerId?: string;
  reviewerName?: string;
}

/**
 * Idempotent open. Returns the existing submission if any; otherwise
 * creates an empty draft. The token's targetDocId MUST match the
 * submission id passed here.
 */
export async function openSubmission(input: OpenSubmissionInput): Promise<Review360Submission> {
  if (input.token.targetDocId !== input.submissionId) {
    throw new Review360Error(
      'unauthorized',
      'Token targetDocId does not match submission id.',
    );
  }
  if (input.token.action !== 'review360_submit') {
    throw new Review360Error('unauthorized', 'Token action is not review360_submit.');
  }

  const ref = doc(db, COL, input.submissionId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { id: snap.id, ...(snap.data() as Omit<Review360Submission, 'id'>) };
  }

  // NOTE: submittedAt is intentionally ABSENT on a fresh draft. It is set
  // exactly once by finaliseSubmission. Writing it here was the bug that
  // sealed every draft at birth: patchSubmission's `if (sub.submittedAt)`
  // guard and the firestore.rules update gate both read it as "already
  // finalised", so every anonymous auto-save was rejected.
  const base: Omit<Review360Submission, 'id'> = {
    cycleId:             input.cycleId,
    revieweeId:          input.revieweeId,
    invitationId:        input.token.id,
    relationship:        input.relationship,
    effectiveVisibility: input.effectiveVisibility,
    valueScores:         {},
    createdAt:           serverTimestamp() as unknown as Timestamp,
    ...(input.effectiveVisibility === 'attributed' && input.reviewerId
        ? { reviewerId: input.reviewerId, reviewerName: input.reviewerName }
        : {}),
  };
  await setDoc(ref, base);
  const created = await getDoc(ref);
  return { id: created.id, ...(created.data() as Omit<Review360Submission, 'id'>) };
}

// ─── Patch fields (auto-save) ───────────────────────────────────────

export interface Review360Patch {
  valueScore?:        { key: CompanyValueKey; score: Score1to5 | null };
  strengths?:         string;
  thingsToImprove?:   string;
  overallComments?:   string;
}

export async function patchSubmission(submissionId: string, patch: Review360Patch): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, submissionId);
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Review360Error('not_found', 'Submission not found.');
    }
    const sub = snap.data() as Review360Submission;
    // Once finaliseSubmission has set submittedAt, the doc is sealed
    // against further reviewer edits. The previous check also required a
    // non-existent `finalSubmittedAt` field — meaning the service-level
    // lock was dead code and the Firestore rule was the sole gate.
    // Single signal is enough; rule is the second line of defence.
    if (sub.submittedAt) {
      throw new Review360Error('wrong_state', 'Submission already finalised.');
    }

    const update: Record<string, any> = {};
    if (patch.valueScore) {
      const nextScores = { ...sub.valueScores };
      if (patch.valueScore.score == null) {
        delete nextScores[patch.valueScore.key];
      } else {
        nextScores[patch.valueScore.key] = patch.valueScore.score;
      }
      update.valueScores = nextScores;
    }
    if (typeof patch.strengths === 'string')         update.strengths = patch.strengths;
    if (typeof patch.thingsToImprove === 'string')   update.thingsToImprove = patch.thingsToImprove;
    if (typeof patch.overallComments === 'string')   update.overallComments = patch.overallComments;
    update.updatedAt = serverTimestamp();
    tx.update(ref, update);
  });
}

// ─── Finalise submission ────────────────────────────────────────────

/**
 * Mark a 360 submission as finalised. Unlike EPR, 360 has no strict required
 * fields — blank value scores are allowed when the reviewer cannot judge.
 * We do require AT LEAST one signal: either one valueScore set OR one of
 * the three free-text fields filled, otherwise the submission is a no-op.
 */
export async function finaliseSubmission(submissionId: string): Promise<Review360Submission> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, submissionId);
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Review360Error('not_found', 'Submission not found.');
    }
    const sub = snap.data() as Review360Submission;

    const hasAnyValue = Object.keys(sub.valueScores ?? {}).length > 0;
    const hasAnyText  = (sub.strengths?.trim() ?? '') !== ''
      || (sub.thingsToImprove?.trim() ?? '') !== ''
      || (sub.overallComments?.trim() ?? '') !== '';
    if (!hasAnyValue && !hasAnyText) {
      throw new Review360Error(
        'wrong_state',
        'Fill in at least one score or one note before submitting.',
      );
    }

    tx.update(ref, {
      submittedAt: serverTimestamp(),
      updatedAt:   serverTimestamp(),
    });
    return { ...sub, id: submissionId, submittedAt: Timestamp.fromDate(new Date()) };
  });
}

// ─── Read by reviewee (HR-side, raw) ────────────────────────────────

export async function listSubmissionsForReviewee(
  cycleId: string,
  revieweeId: string,
): Promise<Review360Submission[]> {
  const q = query(
    collection(db, COL),
    where('cycleId', '==', cycleId),
    where('revieweeId', '==', revieweeId),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Review360Submission, 'id'>) }));
}

export async function getSubmission(submissionId: string): Promise<Review360Submission | null> {
  const snap = await getDoc(doc(db, COL, submissionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Review360Submission, 'id'>) };
}
